'use strict';

/**
 * T207 — client-side auth proof (plaintext-password elimination)
 *
 * Single source of truth for the login/signup/recovery auth verifier. The
 * browser derives this proof and submits it to the server INSTEAD of the raw
 * password, so the server never has transient possession of the secret that
 * derives the master key (CLAUDE.md §Legal Foundation — custodian of
 * ciphertext, not custodian of data; HARDEN-4 browser-only signing).
 *
 * This is NOT new crypto — it reuses the EXACT Argon2id keyPw the unwrap path
 * already computes (argon2id(password, saltPw)) plus one HKDF label for domain
 * separation. The proof is a one-way function of the password:
 *
 *   keyPw = Argon2id(password, saltPw)            // already derived for unwrap
 *   proof = HKDF(keyPw, "oyster:auth-proof:v1")   // distinct label
 *
 * Binding to saltPw (the per-account vault salt already stored in meta) means
 * the proof is account-specific and the server cannot replay it against a
 * different account. The proof reveals nothing about keyPw (HKDF is one-way),
 * so disclosure of the auth proof does NOT help an attacker unwrap the vault.
 *
 * Pure function — no I/O, no state. Browser-safe (only @noble/* via kdf).
 */

const { argon2id, hkdfSubkey, KDF_PROFILES } = require('./kdf');

const AUTH_PROOF_LABEL = 'oyster:auth-proof:v1';
const AUTH_PROOF_PREFIX = 'argon2-proof';

function toSaltBytes(saltPw) {
  if (saltPw instanceof Uint8Array) return saltPw;
  if (typeof saltPw === 'string' && saltPw.length >= 32) {
    return Buffer.from(saltPw, 'hex');
  }
  throw new Error('Invalid saltPw: must be a hex string of at least 16 bytes (or Uint8Array)');
}

/**
 * Derive the client-side auth proof for a password + account salt.
 *
 * @param {object} args
 * @param {string} args.password — the user's password (never leaves the browser)
 * @param {string|Uint8Array} args.saltPw — the per-account vault salt (meta.saltPw)
 * @param {object} [args.kdfProfile] — Argon2id parameters (defaults to DESKTOP)
 * @returns {string} hex-encoded 32-byte proof
 */
function deriveAuthProof({ password, saltPw, kdfProfile }) {
  if (typeof password !== 'string' || password.length === 0) {
    throw new Error('Invalid password: must be a non-empty string');
  }
  const saltBytes = toSaltBytes(saltPw);
  const keyPw = argon2id(password, saltBytes, kdfProfile || KDF_PROFILES.DESKTOP);
  const proof = hkdfSubkey(keyPw, AUTH_PROOF_LABEL, 32);
  return Buffer.from(proof).toString('hex');
}

module.exports = {
  deriveAuthProof,
  AUTH_PROOF_LABEL,
  AUTH_PROOF_PREFIX,
};
